import { create } from "zustand";

// ---------------------------------------------------------------------------
// Phase 4 of STATE_MANAGEMENT_MIGRATION_PLAN.md
//
// Replaces the mutually-exclusive panel booleans and activeSetting string
// from userSettingsSlice in Redux. The three panel open flags
// (userSettingsOpen, organizationSettingsOpen, applicationToolsOpen) are
// modelled as a discriminated union — only one panel can be open at a time.
//
// userSettingsOpen drives side-effects in useMapRequestManager and
// useChartDataController (batch-on-close refetch pattern), so it must live
// in a cross-tree store rather than local state or React Context.
// ---------------------------------------------------------------------------

/** Identifies which settings panel is currently open (or none). */
export type ActivePanel =
  | "userSettings"
  | "organizationSettings"
  | "applicationTools"
  | null;

interface PanelState {
  /** Which panel overlay is currently open (null = all closed). */
  readonly activePanel: ActivePanel;

  /**
   * The active sub-section within the open panel (e.g. "Profile",
   * "OrganizationMembers", "Subscriptions"). Empty string when no panel
   * is open.
   */
  readonly activeSetting: string;
}

interface PanelActions {
  /**
   * Open a specific panel and navigate to a section within it.
   * Automatically closes any other open panel (mutual exclusivity).
   */
  openPanel: (panel: NonNullable<ActivePanel>, setting: string) => void;

  /** Close whichever panel is currently open. */
  closePanel: () => void;

  /**
   * Change the active sub-section without changing which panel is open.
   * No-op if no panel is open.
   */
  setActiveSetting: (setting: string) => void;

  /** Reset the store back to its initial state (e.g. on signout). */
  reset: () => void;
}

export type PanelStore = PanelState & PanelActions;

const initialState: PanelState = {
  activePanel: null,
  activeSetting: ""
};

export const usePanelStore = create<PanelStore>()((set) => ({
  ...initialState,

  openPanel: (panel, setting) => set({ activePanel: panel, activeSetting: setting }),

  closePanel: () => set({ activePanel: null, activeSetting: "" }),

  setActiveSetting: (setting) =>
    set((state) => (state.activePanel !== null ? { activeSetting: setting } : state)),

  reset: () => set(initialState)
}));

// ---------------------------------------------------------------------------
// Named selectors — keep component subscriptions narrow
// ---------------------------------------------------------------------------

/** Whether the User Settings panel is currently open (drives map + chart refetch on close). */
export const selectUserSettingsOpen = (state: PanelStore): boolean =>
  state.activePanel === "userSettings";

/** Whether the Organization Settings panel is currently open. */
export const selectOrganizationSettingsOpen = (state: PanelStore): boolean =>
  state.activePanel === "organizationSettings";

/** Whether the Application Tools panel is currently open. */
export const selectApplicationToolsOpen = (state: PanelStore): boolean =>
  state.activePanel === "applicationTools";

/** The active sub-section label within whichever panel is open. */
export const selectActiveSetting = (state: PanelStore): string => state.activeSetting;

/** Which panel is open, or null when none is. */
export const selectActivePanel = (state: PanelStore): ActivePanel => state.activePanel;
